import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Twitter, Github } from "lucide-react";
import { MagneticSpotlightButton } from "@/components/PremiumAnimations";

const footerServices = [
  { label: "Full-Stack Development", path: "/services/full-stack-development" },
  { label: "AI/ML Integration", path: "/services/ai-ml-integration" },
  { label: "FinTech Infrastructure", path: "/services/fintech-infrastructure" },
  { label: "Trading Platforms", path: "/services/trading-platform-engineering" },
  { label: "Web3 & Blockchain", path: "/services/web3-blockchain" },
  { label: "Cloud & DevOps", path: "/services/cloud-devops" },
  { label: "API Platforms", path: "/services/api-platform-development" },
  { label: "SaaS Development", path: "/services/saas-development" },
];

const footerCompany = [
  { label: "About Us", path: "/about" },
  { label: "Our Approach", path: "/approach" },
  { label: "Case Studies", path: "/work" },
  { label: "Services", path: "/services" },
  { label: "Industries", path: "/industries" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

const footerResources = [
  { label: "Blog", path: "/blog" },
  { label: "ROI Calculator", path: "/resources/roi-calculator" },
  { label: "Tech Stack Quiz", path: "/resources/tech-quiz" },
  { label: "Resources Hub", path: "/resources" },
];

export const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-[#080C10] border-t border-white/5">
      {/* Compact CTA Banner */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="py-12 px-10 md:px-16 rounded-xl border border-white/5 bg-white/[0.02] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-primary/5 rounded-bl-full pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
                Let's Build<span className="gradient-text-teal">.</span>
              </h2>
              <p className="text-base text-slate-400">Ready to start your project? Free 45-minute consultation, no obligation.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <MagneticSpotlightButton
                as={Link}
                to="/contact"
                data-testid="footer-cta-consultation"
                className="inline-flex items-center gap-2 !px-7 py-3.5 !text-sm !font-bold rounded transition-colors duration-200"
                strength={0.15}
              >
                <span>Schedule Free Consultation</span>
                <ArrowUpRight size={15} />
              </MagneticSpotlightButton>
              <a
                href="mailto:hello@venury.tech"
                data-testid="footer-cta-email"
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded transition-colors duration-200"
              >
                hello@venury.tech
              </a>
            </div>
          </div>
        </div>

        {/* Footer Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-10 pt-16 border-t border-white/5">
          <div className="col-span-2 lg:col-span-2 overflow-visible">
            <Link to="/" className="inline-block mb-6 group">
              <img src="/venury_logo.png" alt="Venury Logo" className="h-[100px] md:h-[140px] w-auto object-contain flex-shrink-0 scale-[1.8] origin-left transition-transform duration-300 group-hover:scale-[1.85]" />
            </Link>
            <p className="text-sm text-slate-400 mb-6 font-medium max-w-sm">
              AI-augmented development studio building products for funded startups.
            </p>
          </div>

          <div>
            <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest mb-6">Services</h4>
            <ul className="space-y-3">
              {footerServices.map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className="text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest mb-6">Company</h4>
            <ul className="space-y-3">
              {footerCompany.map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className="text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest mb-6">Resources</h4>
            <ul className="space-y-3">
              {footerResources.map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className="text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest mb-6">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:hello@venury.tech" className="text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                  hello@venury.tech
                </a>
              </li>
              <li>
                <a href="mailto:careers@venury.tech" className="text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                  careers@venury.tech
                </a>
              </li>
              <li className="pt-3 mt-3 border-t border-white/5">
                <span className="text-sm text-slate-400">Bangalore, India</span>
              </li>
              <li>
                <a href="https://venury.tech" className="text-sm text-teal-bright hover:text-white transition-colors duration-200">
                  venury.tech
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-12 mt-12 border-t border-white/5 gap-4">
          <p className="text-xs text-slate-600">
            &copy; 2026 Venury Softech Pvt Ltd. All rights reserved.
          </p>
            {/* TODO: Add real social media links once accounts are set up */}
            {/* <div className="flex items-center gap-6">
              <a href="https://linkedin.com/company/venury" data-testid="footer-linkedin" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-bright transition-colors duration-200">
                <Linkedin size={18} />
              </a>
              <a href="https://twitter.com/venury_tech" data-testid="footer-twitter" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-bright transition-colors duration-200">
                <Twitter size={18} />
              </a>
              <a href="https://github.com/venury" data-testid="footer-github" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-bright transition-colors duration-200">
                <Github size={18} />
              </a>
            </div> */}
          <div className="flex items-center gap-6 text-xs text-slate-600">
            <span className="hover:text-slate-400 cursor-pointer transition-colors duration-200">Privacy Policy</span>
            <span className="hover:text-slate-400 cursor-pointer transition-colors duration-200">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
